import type { Moment } from 'moment'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FullTrip, GeoPlace, Place, PlaceGeo } from 'tripPlanner/types/common'
import type { TripItem } from 'tripPlanner/types/tripItem'

export const CLASSIC_TRIP_MIGRATION_SCHEMA_VERSION = 'classic-trip-to-chat-v1'
export const CLASSIC_TRIP_MIGRATION_QUERY_PARAM = 'classicTripMigration'
export const CLASSIC_TRIP_MIGRATION_PROMPT_PREFIX = 'Please migrate this classic Trip Planner itinerary into AI TripPlanner.'

const CLASSIC_TRIP_MIGRATION_PROMPT_STORAGE_PREFIX = 'tripPlanner:classicTripMigrationPrompt:'

type JsonValue =
  | string
  | number
  | boolean
  | null
  | Array<JsonValue>
  | { [key: string]: JsonValue }

interface MigrationPlace {
  name: string
  region?: string
  luxuryEscapesPlace?: {
    cityName?: string
    cityCode?: string
    countryName?: string
  }
}

interface MigrationTripLocation {
  name: string
  cityCode?: string
  countryCode?: string
}

interface MigrationItem {
  type: string
  name: string
  title?: string
  leOfferId?: string
  notes?: string
  startUtcDateTime?: string
  endUtcDateTime?: string
  dayPlacement?: {
    startDay?: number
    endDay?: number
  }
  booked?: {
    isBooked?: boolean
  }
  placeData: {
    place?: MigrationPlace
    startPlace?: MigrationPlace
    endPlace?: MigrationPlace
  }
}

export interface ClassicTripMigrationPayload {
  schemaVersion: typeof CLASSIC_TRIP_MIGRATION_SCHEMA_VERSION
  generatedAt: string
  instructions: string
  source: {
    product: 'classic_trip_planner'
    tripId: string
    tripName: string
  }
  trip: {
    description?: string
    startDate?: string
    endDate?: string
    sourceOrderId?: string
    sourceCartId?: string
    sourceOfferId?: string
    origin?: MigrationTripLocation
    destinations: Array<string>
  }
  items: Array<MigrationItem>
}

interface StagedMigrationPrompt {
  prompt: string
  createdAt: string
}

const stagedMigrationPromptMemory = new Map<string, StagedMigrationPrompt>()

function getMigrationPromptStorageKey(token: string): string {
  return `${CLASSIC_TRIP_MIGRATION_PROMPT_STORAGE_PREFIX}${token}`
}

function createMigrationPromptToken(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export function stageClassicTripMigrationPrompt(prompt: string): string {
  const token = createMigrationPromptToken()
  const stagedPrompt: StagedMigrationPrompt = {
    prompt,
    createdAt: new Date().toISOString(),
  }

  try {
    window.sessionStorage.setItem(
      getMigrationPromptStorageKey(token),
      JSON.stringify(stagedPrompt),
    )
  } catch {
    stagedMigrationPromptMemory.set(token, stagedPrompt)
  }

  return token
}

export function consumeClassicTripMigrationPrompt(token: string | null | undefined): string | undefined {
  if (!token) return undefined

  const storageKey = getMigrationPromptStorageKey(token)

  if (typeof window !== 'undefined') {
    try {
      const stagedPrompt = JSON.parse(window.sessionStorage.getItem(storageKey) ?? 'null') as StagedMigrationPrompt | null
      window.sessionStorage.removeItem(storageKey)

      if (typeof stagedPrompt?.prompt === 'string') {
        return stagedPrompt.prompt
      }
    } catch {
      try {
        window.sessionStorage.removeItem(storageKey)
      } catch {
        // no-op: storage may be unavailable, in which case the in-memory fallback is used.
      }
    }
  }

  const stagedPrompt = stagedMigrationPromptMemory.get(token)
  stagedMigrationPromptMemory.delete(token)

  return typeof stagedPrompt?.prompt === 'string' ? stagedPrompt.prompt : undefined
}

function compactObject<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T
}

function compactObjectOrUndefined<T extends Record<string, unknown>>(
  value: T,
): T | undefined {
  const compactedValue = compactObject(value)

  return Object.keys(compactedValue).length ? compactedValue : undefined
}

function formatDate(date?: Moment): string | undefined {
  return date?.isValid() ? date.format(ISO_DATE_FORMAT) : undefined
}

function formatDateTime(dateTime?: Moment): string | undefined {
  return dateTime?.isValid() ? dateTime.toISOString() : undefined
}

function isGeoPlace(place: Place): place is GeoPlace {
  return place.type === 'GEO'
}

function serializeTripLocation(placeGeo?: PlaceGeo): MigrationTripLocation | undefined {
  if (!placeGeo) return undefined

  return compactObject({
    name: placeGeo.lePlaceName,
    cityCode: placeGeo.cityCode,
    countryCode: placeGeo.countryCode,
  })
}

function serializeTripDestination(placeGeo?: PlaceGeo): string | undefined {
  return placeGeo?.lePlaceName
}

function serializeLuxuryEscapesPlace(place: GeoPlace) {
  if (!place.lePlace) return undefined

  return compactObject({
    cityName: place.lePlace.city.name,
    cityCode: place.lePlace.city.code,
    countryName: place.lePlace.country.name,
  })
}

function serializePlace(place?: Place): MigrationPlace | undefined {
  if (!place) return undefined

  if (isGeoPlace(place)) {
    return compactObject({
      name: place.name,
      region: place.region,
      luxuryEscapesPlace: serializeLuxuryEscapesPlace(place),
    })
  }

  return compactObject({
    name: place.name,
    region: place.region,
  })
}

function getItemName(item: TripItem): string {
  if ('name' in item && item.name) return item.name
  if ('title' in item && item.title) return item.title
  if ('companyName' in item && item.companyName) return item.companyName
  if ('operatorName' in item && item.operatorName) return item.operatorName
  if ('flightNumber' in item && item.flightNumber) return item.flightNumber
  if ('carType' in item && item.carType) return item.carType
  return item.sortableName || item.type
}

function getStartPlace(item: TripItem): Place | undefined {
  if ('startPlace' in item) return item.startPlace
  if ('place' in item) return item.place
  return undefined
}

function getEndPlace(item: TripItem): Place | undefined {
  if ('endPlace' in item) return item.endPlace
  return undefined
}

function hasRoutePlaces(item: TripItem): boolean {
  return 'startPlace' in item || 'endPlace' in item
}

function getSavedItemData(item: TripItem): JsonValue | undefined {
  if (!('savedItemData' in item)) return undefined
  return item.savedItemData as JsonValue | undefined
}

function getStringField(value: unknown, field: string): string | undefined {
  if (!value || typeof value !== 'object' || !(field in value)) return undefined
  const fieldValue = (value as Record<string, unknown>)[field]
  return typeof fieldValue === 'string' && fieldValue ? fieldValue : undefined
}

function getLeOfferId(item: TripItem): string | undefined {
  const savedItemData = getSavedItemData(item)

  return (
    getStringField(savedItemData, 'offerId') ??
    getStringField(savedItemData, 'experienceId') ??
    getStringField(savedItemData, 'id') ??
    ('code' in item ? item.code : undefined)
  )
}

function getItemType(item: TripItem): string {
  return item.sourceType.startsWith('LE_') ? item.sourceType : item.type
}

function getItemDayPlacement(trip: FullTrip, item: TripItem) {
  const endDate = 'endDate' in item ? item.endDate : undefined
  const startDay = item.startDate && trip.startDate ?
    item.startDate.diff(trip.startDate, 'days') + 1 :
    item.startDay
  const endDay = endDate && trip.startDate ?
    endDate.diff(trip.startDate, 'days') + 1 :
    item.endDay

  if (!startDay && !endDay) return undefined

  return compactObject({ startDay, endDay })
}

function getItemNotes(item: TripItem): string | undefined {
  if (!item.insiderTip) return item.notes
  if (!item.notes) return `Insider Tip: ${item.insiderTip}`

  return `${item.notes} Insider Tip: ${item.insiderTip}`
}

function getItemStartDay(trip: FullTrip, item: TripItem): number | undefined {
  return getItemDayPlacement(trip, item)?.startDay
}

function compareNullableNumbers(firstValue?: number, secondValue?: number): number {
  if (firstValue === secondValue) return 0
  if (firstValue === undefined) return 1
  if (secondValue === undefined) return -1

  return firstValue - secondValue
}

function compareNullableStrings(firstValue?: string, secondValue?: string): number {
  if (firstValue === secondValue) return 0
  if (firstValue === undefined) return 1
  if (secondValue === undefined) return -1

  return firstValue.localeCompare(secondValue)
}

function compareTripItemsByItineraryOrder(trip: FullTrip, firstItem: TripItem, secondItem: TripItem): number {
  return compareNullableNumbers(getItemStartDay(trip, firstItem), getItemStartDay(trip, secondItem)) ||
    firstItem.itineraryRankString.localeCompare(secondItem.itineraryRankString) ||
    firstItem.summaryRankString.localeCompare(secondItem.summaryRankString) ||
    compareNullableStrings(formatDateTime(firstItem.startUtcDateTime), formatDateTime(secondItem.startUtcDateTime))
}

function itemIsVisibleInClassicItinerary(item: TripItem): boolean {
  return !item.alternativeGroup || !!item.startDate
}

function serializeItem(trip: FullTrip, item: TripItem): MigrationItem {
  const startPlace = serializePlace(getStartPlace(item))
  const endPlace = serializePlace(getEndPlace(item))
  const routeItem = hasRoutePlaces(item)

  return compactObject({
    type: getItemType(item),
    name: getItemName(item),
    title: 'title' in item ? item.title : undefined,
    leOfferId: getLeOfferId(item),
    notes: getItemNotes(item),
    startUtcDateTime: formatDateTime(item.startUtcDateTime),
    endUtcDateTime: 'endUtcDateTime' in item ? formatDateTime(item.endUtcDateTime) : undefined,
    dayPlacement: getItemDayPlacement(trip, item),
    booked: compactObjectOrUndefined({
      isBooked: 'isBooked' in item ? item.isBooked : undefined,
    }),
    placeData: routeItem ?
        compactObject({
          startPlace,
          endPlace,
        }) :
        compactObject({
          place: startPlace,
        }),
  })
}

export function buildClassicTripMigrationPayload(
  trip: FullTrip,
  generatedAt: Date = new Date(),
): ClassicTripMigrationPayload {
  return {
    schemaVersion: CLASSIC_TRIP_MIGRATION_SCHEMA_VERSION,
    generatedAt: generatedAt.toISOString(),
    instructions: 'Use this exported classic Trip Planner itinerary to recreate the trip in AI TripPlanner. Preserve dates, times, ordering, booked status, places, and Luxury Escapes offer references where available. Use place names and regions as location context. Do not import documents.',
    source: {
      product: 'classic_trip_planner',
      tripId: trip.id,
      tripName: trip.name,
    },
    trip: compactObject({
      description: trip.description,
      startDate: formatDate(trip.startDate),
      endDate: formatDate(trip.endDate),
      sourceOrderId: trip.sourceOrderId,
      sourceCartId: trip.sourceCartId,
      sourceOfferId: trip.sourceOfferId,
      origin: serializeTripLocation(trip.originGeo),
      destinations: trip.destinationsGeo?.map(serializeTripDestination).filter((destination): destination is string => !!destination) ?? [],
    }),
    items: trip.items
      .filter(itemIsVisibleInClassicItinerary)
      .sort((firstItem, secondItem) => compareTripItemsByItineraryOrder(trip, firstItem, secondItem))
      .map((item) => serializeItem(trip, item)),
  }
}

export function buildClassicTripMigrationPrompt(
  trip: FullTrip,
  generatedAt?: Date,
): string {
  const payload = buildClassicTripMigrationPayload(trip, generatedAt)

  return [
    CLASSIC_TRIP_MIGRATION_PROMPT_PREFIX,
    'Create a new chat trip plan that preserves the itinerary structure, dates, places, notes, booked status, and Luxury Escapes offer references.',
    'Use place names and regions as location context. Do not invent missing place details unless the structured place data is incomplete.',
    '',
    '```json',
    JSON.stringify(payload, null, 2),
    '```',
  ].join('\n')
}
